import { useMemo, useState } from "react";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";

import { FocusableTooltip } from "../../../../../components/FocusableTooltip";
import {
  SegmentedControl,
  SegmentedControlOption,
} from "../../../../../components/SegmentedControl";
import { formatPercent, formatSignedPercent } from "../../../../../utils";
import { NotAnnualizedInfo } from "../../../NotAnnualizedInfo";
import { irrListTooltipOf, returnBasisOf } from "../../../utils";
import {
  buildAllocation,
  buildAllocationByType,
  buildReturnsByHolding,
  buildReturnsByType,
  incompleteCashMessageOf,
} from "./insights";
import {
  AllocationFill,
  Body,
  Chevron,
  CollapseButton,
  Column,
  ColumnHeader,
  ColumnSubtitle,
  ColumnTitle,
  EmptyBody,
  EmptyText,
  EmptyTitle,
  Header,
  HeaderControl,
  HeaderSubtitle,
  HeaderTitle,
  Name,
  NameText,
  Panel,
  ReturnFill,
  ReturnTrack,
  Row,
  RowList,
  Swatch,
  Track,
  Value,
} from "./styles";
import { InsightsScope, InvestmentsInsightsProps } from "./types";

const SCOPE_OPTIONS: SegmentedControlOption<InsightsScope>[] = [
  { value: "holding", label: "By holding" },
  { value: "type", label: "By type" },
];

export const InvestmentsInsights: React.FC<InvestmentsInsightsProps> = ({
  investments,
  isCompact,
  returnsByType: returnsByTypeData,
  isValuationComplete,
}) => {
  const [expanded, setExpanded] = useState(!isCompact);
  const [scope, setScope] = useState<InsightsScope>("holding");

  const allocationByHolding = useMemo(
    () => buildAllocation(investments),
    [investments],
  );
  const allocationByType = useMemo(
    () => buildAllocationByType(investments),
    [investments],
  );

  const returnByTypeId = useMemo(
    () =>
      new Map(returnsByTypeData.map((type) => [type.investmentTypeId, type])),
    [returnsByTypeData],
  );

  const returnsByHolding = useMemo(
    () => buildReturnsByHolding(investments),
    [investments],
  );
  const returnsByType = useMemo(
    () => buildReturnsByType(allocationByType, returnByTypeId),
    [allocationByType, returnByTypeId],
  );
  const returnsBasis = useMemo(() => returnBasisOf(investments), [investments]);
  const incompleteCashMessage = useMemo(
    () => incompleteCashMessageOf(investments),
    [investments],
  );

  const isByType = scope === "type";
  const allocation = isByType ? allocationByType : allocationByHolding;
  const returns = isByType ? returnsByType : returnsByHolding;
  const hasAllocation = isValuationComplete && allocation.length > 0;

  // The bar encodes direction (gain right of zero, loss left) and a length
  // relative to the largest return shown, so its scale is named rather than left
  // implicit. "Best to worst" only describes an ordering when there is more than
  // one row to rank; with a single holding or type it would claim nothing.
  const canRankReturns = returns.length > 1;
  const hasPeriodRate = returns.some(({ isPeriodRate }) => isPeriodRate);
  const returnsMeasure = hasPeriodRate
    ? "Return (IRR)"
    : "Annualized return (IRR)";
  const returnsSubtitle = canRankReturns
    ? `${returnsMeasure} · best to worst, bars relative to the largest`
    : returnsMeasure;

  const toggleExpanded = () => {
    setExpanded((current) => !current);
  };

  const holdingCount = investments.length;
  const holdingLabel =
    holdingCount === 1 ? "1 holding" : `${holdingCount} holdings`;

  const returnsContent = (
    <RowList>
      {returns.map((item) => {
        const hasReturn = item.percent !== null;
        return (
          <Row key={item.id}>
            <Name>
              <NameText>{item.name}</NameText>
              {item.isPeriodRate && <NotAnnualizedInfo />}
            </Name>
            <ReturnTrack>
              {hasReturn && (
                <ReturnFill $width={item.barWidth} $tone={item.tone} />
              )}
            </ReturnTrack>
            <Value $tone={item.tone} $muted={!hasReturn}>
              {formatSignedPercent(item.percent)}
            </Value>
          </Row>
        );
      })}
    </RowList>
  );

  const plainReturnsSubtitle = (
    <ColumnSubtitle>{returnsSubtitle}</ColumnSubtitle>
  );
  const returnsSubtitleNode = returnsBasis ? (
    <FocusableTooltip
      title={irrListTooltipOf(returnsBasis.gainTooltip, hasPeriodRate)}
    >
      {plainReturnsSubtitle}
    </FocusableTooltip>
  ) : (
    plainReturnsSubtitle
  );

  const insightsBody = (
    <>
      <Column>
        <ColumnHeader>
          <ColumnTitle>Allocation</ColumnTitle>
          <ColumnSubtitle>Current share of portfolio value</ColumnSubtitle>
        </ColumnHeader>
        <RowList>
          {allocation.map((item) => (
            <Row key={item.id}>
              <Name>
                <Swatch $color={item.color} />
                <NameText>{item.name}</NameText>
              </Name>
              <Track>
                <AllocationFill $width={item.percent} $color={item.color} />
              </Track>
              <Value>{formatPercent(item.percent)}</Value>
            </Row>
          ))}
        </RowList>
      </Column>

      <Column>
        <ColumnHeader>
          <ColumnTitle>Returns</ColumnTitle>
          {returnsSubtitleNode}
        </ColumnHeader>
        {returnsContent}
      </Column>
    </>
  );

  const emptyBody = (
    <EmptyBody>
      <EmptyTitle>Nothing to break down yet</EmptyTitle>
      <EmptyText>
        {!isValuationComplete
          ? "A live valuation is unavailable for at least one holding. Allocation and returns will appear once every holding is valued."
          : (incompleteCashMessage ??
            "These holdings have no current value to allocate. Update a holding's market value to see allocation and returns.")}
      </EmptyText>
    </EmptyBody>
  );

  const bodyContent = hasAllocation ? insightsBody : emptyBody;

  // The breakdown control governs both columns, so it lives in the header
  // opposite the title once expanded; while collapsed the same slot previews the
  // holding count. Only the breakdown control shows when there is data to break
  // down (the empty state has nothing to scope).
  const showBreakdownControl = expanded && hasAllocation;
  const headerAside = showBreakdownControl ? (
    <HeaderControl>
      <SegmentedControl
        options={SCOPE_OPTIONS}
        value={scope}
        onChange={setScope}
        ariaLabel="Breakdown"
        testId="insights-breakdown"
      />
    </HeaderControl>
  ) : (
    <HeaderSubtitle>{holdingLabel}</HeaderSubtitle>
  );

  return (
    <Panel>
      <Header>
        <CollapseButton
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label="Toggle insights section"
        >
          <Chevron $expanded={expanded}>
            <ExpandLessRoundedIcon />
          </Chevron>
          <HeaderTitle>Insights</HeaderTitle>
        </CollapseButton>
        {headerAside}
      </Header>

      {expanded && <Body>{bodyContent}</Body>}
    </Panel>
  );
};
